import type { CardCatalog } from "@pra/shared/catalog";
import { expandEnergyPlaceholders, lookupCard } from "@pra/shared/catalog";
import { useQuery } from "@tanstack/react-query";
import { api } from "./api";

const CACHE_KEY = "pra.catalog.v1";

async function loadCatalog(): Promise<CardCatalog> {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached) as { at: number; data: CardCatalog };
      if (Date.now() - parsed.at < 7 * 86400000 && Object.keys(parsed.data).length > 0)
        return parsed.data;
    }
  } catch {
    /* ignore cache errors */
  }
  const data = await api<CardCatalog>("GET", "/api/catalog", undefined, { scoped: false });
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), data }));
  } catch {
    /* quota exceeded — fine, memory cache still works */
  }
  return data;
}

/** Card catalog (client id → name/set/number). Empty until loaded; ids render raw meanwhile. */
export function useCatalog(): {
  catalog: CardCatalog | undefined;
  name: (ref: { cardId?: string; name?: string }) => string;
  print: (ref: { cardId?: string; set?: string; number?: string }) => string | undefined;
} {
  const query = useQuery({
    queryKey: ["catalog"],
    queryFn: loadCatalog,
    staleTime: Number.POSITIVE_INFINITY,
    retry: 1,
  });
  const catalog = query.data;
  return {
    catalog,
    name: (ref) => {
      if (ref.name) return expandEnergyPlaceholders(ref.name);
      if (ref.cardId === undefined) return "?";
      const found = catalog && lookupCard(catalog, ref.cardId)?.name;
      return found ? expandEnergyPlaceholders(found) : ref.cardId;
    },
    print: (ref) => {
      if (ref.set && ref.number) return `${ref.set} ${ref.number}`;
      if (ref.cardId === undefined || catalog === undefined) return undefined;
      const card = lookupCard(catalog, ref.cardId);
      return card === undefined ? undefined : `${card.set} ${card.number}`;
    },
  };
}
